import {Controller} from "@hotwired/stimulus"
import html2canvas from "html2canvas"

export default class extends Controller {
    static targets = ["achievement", "shareBtn", "shareBtnText", "closeBtn"]
    static values = {
        title: String,
        downloadText: String
    }

    connect() {
        this.checkSupport()
    }

    async share(event) {
        event.preventDefault()
        this.startLoading()

        try {
            const imageFile = await this.generateImage()

            if (navigator.share && navigator.canShare && navigator.canShare({ files: [imageFile] })) {
                try {
                    await navigator.share({
                        title: this.titleValue,
                        text: this.titleValue,
                        files: [imageFile]
                    })
                } catch (shareErr) {
                    if (shareErr.name !== 'AbortError') {
                        this.downloadImage(imageFile)
                    }
                }
            }
            else {
                this.downloadImage(imageFile)
            }
        } catch (err) {
            console.error(err)
        } finally {
            this.stopLoading()
        }
    }

    async generateImage() {
        if (!this.hasAchievementTarget) {
            throw new Error('Achievement target not found')
        }

        const canvas = await html2canvas(this.achievementTarget, {
            backgroundColor: '#141619',
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: false,
            imageTimeout: 0
        })

        const blob = await new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/png', 0.95)
        })

        const fileName = `achievement-level-${new Date().getTime()}.png`
        return new File([blob], fileName, {
            type: 'image/png',
            lastModified: new Date().getTime()
        })
    }

    downloadImage(file) {
        const reader = new FileReader()

        reader.onload = (e) => {
            const dataUrl = e.target.result

            const a = document.createElement('a')
            a.href = dataUrl
            a.download = file.name
            a.style.display = 'none'

            document.body.appendChild(a)
            a.click()

            setTimeout(() => {
                document.body.removeChild(a)
                this.showDownloadFeedback()
            }, 100)
        }

        reader.readAsDataURL(file)
    }

    showDownloadFeedback() {
        if (this.hasShareBtnTarget) {
            const textSpan = this.shareBtnTarget.querySelector('.app-share-modal__button-text')
            const originalText = textSpan?.textContent || ''

            this.shareBtnTarget.classList.add('app-share-modal__button--copied')

            setTimeout(() => {
                if (textSpan) {
                    textSpan.textContent = originalText
                }
                this.shareBtnTarget.classList.remove('app-share-modal__button--copied')
            }, 2000)
        }
    }

    startLoading() {
        if (this.hasShareBtnTarget) {
            this.shareBtnTarget.classList.add('app-modal__button--loading')
            this.shareBtnTarget.disabled = true
        }
    }

    stopLoading() {
        if (this.hasShareBtnTarget) {
            this.shareBtnTarget.classList.remove('app-modal__button--loading')
            this.shareBtnTarget.disabled = false
        }
    }

    checkSupport() {
        let supportsShare = false

        try {
            if (navigator.canShare && navigator.canShare({ files: [] })) {
                supportsShare = true
            } else if (navigator.share) {
                supportsShare = true
            }
        } catch (e) {
            supportsShare = !!navigator.share
        }

        if (!supportsShare) {
            if(this.hasShareBtnTextTarget) {
                this.shareBtnTextTarget.innerHTML = this.downloadTextValue
            }
        }

        this.dispatch("support", {
            detail: { supported: supportsShare },
            bubbles: true
        })
    }
}
